import React from 'react';
import { Button, Checkbox, InputNumber, Table, Tag } from 'antd';

/** 轨迹数据管理（指挥系统设置子页） */
export function buildTrackManagementTab(context) {
  const {
    shouldShowPage,
    trackStats,
    trackSettings,
    setTrackSettings,
    saveTrackSettings,
    trackSettingsLoading,
    cleanupTrackData,
    trackSettingsMsg,
    trackSessions
  } = context;

  if (
    !shouldShowPage?.('轨迹数据管理')
    && !shouldShowPage?.('指挥系统设置')
    && !shouldShowPage?.('系统设置')
  ) {
    return null;
  }

  return {
    key: 'track_management',
    label: '轨迹管理',
    children: (
      <div>
        <h3>轨迹数据管理</h3>
        <p style={{ color: '#666', marginBottom: 16 }}>
          管理指挥侧轨迹保留与清理策略。危险清理需输入确认短语。
        </p>

        {trackStats && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
            <div style={{ background: '#f0f8ff', padding: 12, borderRadius: 4, textAlign: 'center' }}>
              <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#1890ff' }}>
                {trackStats.total_points?.toLocaleString() || 0}
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>总轨迹点数</div>
            </div>
            <div style={{ background: '#f6ffed', padding: 12, borderRadius: 4, textAlign: 'center' }}>
              <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#52c41a' }}>
                {trackStats.total_members || 0}
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>参与队员数</div>
            </div>
            <div style={{ background: '#f9f0ff', padding: 12, borderRadius: 4, textAlign: 'center' }}>
              <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#722ed1' }}>
                {trackStats.total_sessions || 0}
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>轨迹会话数</div>
            </div>
            <div style={{ background: '#fff7e6', padding: 12, borderRadius: 4, textAlign: 'center' }}>
              <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#fa8c16' }}>
                {trackStats.active_sessions || 0}
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>活跃会话数</div>
            </div>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
          <div>
            <h5>轨迹数据保留天数</h5>
            <InputNumber
              value={trackSettings.track_retention_days}
              onChange={value => setTrackSettings(prev => ({ ...prev, track_retention_days: value }))}
              min={1}
              max={365}
              style={{ width: '100%' }}
            />
          </div>
          <div>
            <h5>每个会话最大轨迹点数</h5>
            <InputNumber
              value={trackSettings.max_track_points_per_session}
              onChange={value => setTrackSettings(prev => ({ ...prev, max_track_points_per_session: value }))}
              min={100}
              max={100000}
              style={{ width: '100%' }}
            />
          </div>
          <div>
            <h5>暂时离线判定（分钟）· 橙灯</h5>
            <InputNumber
              value={trackSettings.member_temp_offline_minutes
                ?? trackSettings.member_online_minutes
                ?? 2}
              onChange={value => setTrackSettings(prev => ({
                ...prev,
                member_temp_offline_minutes: value,
                member_online_minutes: value
              }))}
              min={1}
              max={120}
              style={{ width: '100%' }}
            />
            <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
              指挥页按 last_seen 静默时长约每 5 秒重算：静默超过此时长 → 橙灯「暂时离线」（不增加队员端耗电）。
              建议约为设备正常上报间隔的 1.5～2 倍。默认 2 分钟。
            </div>
          </div>
          <div>
            <h5>正式离线判定（分钟）· 灰灯</h5>
            <InputNumber
              value={trackSettings.member_offline_minutes ?? 15}
              onChange={value => setTrackSettings(prev => ({
                ...prev,
                member_offline_minutes: value
              }))}
              min={2}
              max={1440}
              style={{ width: '100%' }}
            />
            <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
              静默超过此时长 → 灰灯「正式离线」。须大于「暂时离线」分钟数。默认 15 分钟。
            </div>
          </div>
          <div>
            <h5>实时绘制离线宽限（分钟）</h5>
            <InputNumber
              value={trackSettings.realtime_track_offline_grace_minutes ?? 30}
              onChange={value => setTrackSettings(prev => ({
                ...prev,
                realtime_track_offline_grace_minutes: value
              }))}
              min={1}
              max={1440}
              style={{ width: '100%' }}
            />
            <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
              已开启「绘制轨迹」时：无上报后仍继续续画；超过此时长才停止绘制（地图线条保留）。可与正式离线时长不同。
            </div>
          </div>
          <div>
            <h5>队员地图图标 · 按速度自动切换</h5>
            <Checkbox
              checked={trackSettings.member_marker_speed_auto !== false}
              onChange={(e) => setTrackSettings((prev) => ({
                ...prev,
                member_marker_speed_auto: e.target.checked
              }))}
            >
              根据上报速度自动匹配水滴内图案（静止/徒步/跑步/自行车/摩托/汽车/动车/飞机）
            </Checkbox>
            <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
              关闭后使用队员手动选择的图标。地图坐标默认灰色水滴；状态为浅蓝（在线）/ 橙（暂时离线）/ 红（选中）/ 深蓝（高亮）/ 灰（离线）。
              速度阈值约：静止&lt;0.4m/s · 徒步&lt;2 · 跑&lt;4.5 · 骑&lt;8.5 · 摩&lt;16 · 车&lt;35 · 动车&lt;70 · 以上飞机。
            </div>
          </div>
          <div>
            <h5>启用自动清理</h5>
            <Checkbox
              checked={trackSettings.auto_cleanup_enabled}
              onChange={e => setTrackSettings(prev => ({ ...prev, auto_cleanup_enabled: e.target.checked }))}
            >
              启用自动清理过期轨迹数据
            </Checkbox>
          </div>
          <div>
            <h5>启用轨迹压缩</h5>
            <Checkbox
              checked={trackSettings.track_compression_enabled}
              onChange={e => setTrackSettings(prev => ({ ...prev, track_compression_enabled: e.target.checked }))}
            >
              启用轨迹数据压缩
            </Checkbox>
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <Button type="primary" onClick={saveTrackSettings} loading={trackSettingsLoading} style={{ marginRight: 8 }}>
            保存轨迹设置
          </Button>
          <Button danger onClick={cleanupTrackData} style={{ marginRight: 8 }}>
            清理过期数据
          </Button>
          <Button
            onClick={() => {
              setTrackSettings({
                track_retention_days: 30,
                auto_cleanup_enabled: true,
                max_track_points_per_session: 10000,
                track_compression_enabled: true,
                track_export_formats: 'gpx,kml,json,csv',
                realtime_track_offline_grace_minutes: 30,
                member_temp_offline_minutes: 5,
                member_online_minutes: 2,
                member_offline_minutes: 15,
                member_marker_speed_auto: true
              });
            }}
          >
            重置默认
          </Button>
        </div>

        {trackSettingsMsg && (
          <div style={{
            color: trackSettingsMsg.includes('成功') ? 'green' : 'red',
            marginBottom: 16,
            padding: 8,
            background: trackSettingsMsg.includes('成功') ? '#f6ffed' : '#fff2f0',
            border: `1px solid ${trackSettingsMsg.includes('成功') ? '#b7eb8f' : '#ffccc7'}`,
            borderRadius: 4
          }}>
            {trackSettingsMsg}
          </div>
        )}

        {(trackSessions || []).length > 0 && (
          <div>
            <h5 style={{ marginBottom: 12 }}>最近轨迹会话</h5>
            <Table
              dataSource={trackSessions || []}
              columns={[
                { title: '队员', dataIndex: 'member_name', key: 'member_name', width: 100 },
                {
                  title: '类型',
                  dataIndex: 'session_type',
                  key: 'session_type',
                  width: 80,
                  render: (type) => {
                    const colorMap = { draw: 'blue', receive: 'green', both: 'purple' };
                    return <Tag color={colorMap[type]}>{type}</Tag>;
                  }
                },
                {
                  title: '状态',
                  dataIndex: 'status',
                  key: 'status',
                  width: 80,
                  render: (status) => {
                    const colorMap = { active: 'green', completed: 'blue', cancelled: 'red' };
                    return <Tag color={colorMap[status]}>{status}</Tag>;
                  }
                },
                {
                  title: '开始时间',
                  dataIndex: 'start_time',
                  key: 'start_time',
                  width: 150,
                  render: (text) => new Date(text).toLocaleString()
                },
                { title: '轨迹点数', dataIndex: 'point_count', key: 'point_count', width: 80 }
              ]}
              pagination={false}
              size="small"
              rowKey="session_id"
            />
          </div>
        )}
      </div>
    )
  };
}
